import * as Sentry from '@sentry/vue';
import { getItemFromLocalStorage, setItemInLocalStorage } from '@utils/web-storage';
import { CheckoutSuccess } from 'types/checkout';
import { datadogRum } from '@datadog/browser-rum';
import mixpanel from 'mixpanel-browser';
import { MixpanelIdDto } from 'types/analytics';
import { updateMixpanelId } from '@api/analytics';
import { useAppStore } from '@stores/app';
import { userAgentIsBot } from '@utils/bot-detection';
import { useUserStore } from '@stores/user';

export function hasFacebookQuery(): boolean {
	const appStore = useAppStore();
	return !!window.fbq && !appStore.isThirdPartyLogin;
}

export function hasGoogleAnalytics(): boolean {
	const appStore = useAppStore();
	return !!window.ga && !appStore.isThirdPartyLogin;
}

export function hasMixpanel(): boolean {
	const appStore = useAppStore();
	return appStore.mixpanelInitialized;
}

function initialTracking(): void {
	if (!mixpanel.get_property('Initial Page View')) {
		mixpanel.track('Initial Page View', {
			'page name': document.title,
			url: window.location.pathname
		});
		mixpanel.register_once({ 'Initial Page View': new Date().getTime() });
	}
}

export const initializeMixpanel = () => {
	const appStore = useAppStore();

	mixpanel.init(`${import.meta.env.VITE_MIXPANEL_TOKEN}`, {
		api_host: `${import.meta.env.VITE_MIXPANEL_WEB_API_HOST}`,
		persistence: appStore.isMobileWebview ? 'cookie' : 'localStorage',
		disable_notifications: true,
		api_method: 'GET',
		batch_requests: false,
		store_google: false,
		ignore_dnt: true,
		debug: import.meta.env.VITE_DEBUG_MIXPANEL === 'true',
		loaded: (mxp) => {
			appStore.mixpanelInitialized = true;
			const distinctId = mxp.get_distinct_id();
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({ mixpanel_id: distinctId });
			setItemInLocalStorage('distinctid', distinctId);
			datadogRum.setUser({
				id: distinctId,
				mixpanelId: distinctId
			});
			Sentry.setUser({
				mixpanelId: distinctId
			});

			const distinctIdFromQueryParam = sessionStorage.getItem('distinctIdFromQueryParam');
			sessionStorage.removeItem('distinctIdFromQueryParam');

			if (userAgentIsBot()) {
				mixpanel.register({ $ignore: true }); // https://docs.mixpanel.com/docs/debugging/bot-traffic
				return;
			}

			if (distinctIdFromQueryParam) {
				identifyMixpanel(distinctIdFromQueryParam).then(() => {
					initialTracking();
				});
			} else {
				initialTracking();
			}
		}
	});
};

export const getDistinctId = (): string | undefined => {
	if (hasMixpanel() && typeof mixpanel.get_distinct_id == 'function') {
		return mixpanel.get_distinct_id();
	}
	const userStore = useUserStore();
	return getItemFromLocalStorage('distinctid') ?? userStore.user?.mixpanelId ?? undefined;
};

export const identifyMixpanel = async (distinctid: string): Promise<void> => {
	const currentDistinctId = getDistinctId();

	if (currentDistinctId !== distinctid) {
		const updateDto: MixpanelIdDto = {
			oldMixpanelId: `${currentDistinctId}`,
			newMixpanelId: distinctid
		};
		Sentry.addBreadcrumb({
			category: 'mixpanel',
			message: 'Identify Mixpanel',
			level: 'info',
			data: updateDto
		});

		if (currentDistinctId && distinctid) {
			await updateMixpanelId(updateDto);
		}

		setItemInLocalStorage('distinctid', distinctid);
		if (hasMixpanel()) {
			const appStore = useAppStore();
			mixpanel.identify(distinctid);
			await appStore.initializeLaunchDarklyInstance();
		}
		if (window.gtag) {
			window.gtag({ mixpanel_id: distinctid });
		}
	}
};

/* eslint-disable @typescript-eslint/no-explicit-any */
export const registerMixpanel = (properties: any): void => {
	if (hasMixpanel()) {
		mixpanel.register(properties);
	}
};
/* eslint-enable @typescript-eslint/no-explicit-any */

const trackUetqConversion = (checkoutSuccess: CheckoutSuccess): void => {
	window.uetq = window.uetq || [];
	window.uetq.push('event', 'Initial Investment Placement', {
		event_category: 'Investor',
		event_label: 'Landing Page',
		event_value: '1',
		revenue_value: checkoutSuccess.investmentAmount,
		currency: 'USD'
	});
};

const trackGoogleAnalyticsConversion = (checkoutSuccess: CheckoutSuccess): void => {
	const investmentType = checkoutSuccess.firstInvestmentForUser ? 'First Time Investment' : 'Repeat Investment';
	if (hasGoogleAnalytics()) {
		window.ga('send', 'event', {
			eventCategory: 'Investment',
			eventAction: 'Complete',
			eventLabel: 'REIT',
			eventValue: checkoutSuccess.investmentAmount
		});

		window.ga('send', 'event', {
			eventCategory: investmentType,
			eventAction: 'Complete',
			eventLabel: 'REIT',
			eventValue: checkoutSuccess.investmentAmount
		});
	}
};

const trackPdstConversion = (checkoutSuccess: CheckoutSuccess): void => {
	if (window.pdst) {
		window.pdst('purchase', {
			value: checkoutSuccess.investmentAmount,
			currency: 'USD',
			discount_code: null,
			order_id: checkoutSuccess.userId
		});
	}
};

const trackGtagConversion = (checkoutSuccess: CheckoutSuccess, totalValueNumber: number): void => {
	const userStore = useUserStore();
	if (window.gtag) {
		window.gtag({
			event: 'conversion',
			value: totalValueNumber,
			currency: 'USD',
			transaction_id: checkoutSuccess.userId,
			...checkoutSuccess.analyticsProperties
		});
	}

	if (window.gtag && userStore.isSubscriptionActive) {
		window.gtag({
			event: 'pro'
		});
	}
};

const trackFacebookQueryConversion = (totalValueWithoutOutliers: number): void => {
	if (hasFacebookQuery()) {
		window.fbq('track', 'Purchase', {
			value: totalValueWithoutOutliers,
			currency: 'USD',
			product_name: 'REIT',
			investor_type: 'new'
		});
	}
};

const trackLetterpressConversion = (checkoutSuccess: CheckoutSuccess): void => {
	if (window.letterpress) {
		const address = checkoutSuccess.userAddress;
		window.letterpress(
			'addTrans',
			checkoutSuccess.userId, // order ID
			'', // affiliation or store name
			checkoutSuccess.investmentAmount, // grand total
			'0', // tax amount
			'0', // shipping amount
			address ? address.city : '', // city
			address ? address.state : '', // state or province
			'USA', // country
			'USD', // currency
			[
				{
					schema: 'iglu:com.getletterpress/address/jsonschema/1-0-0',
					data: {
						name: checkoutSuccess.userName,
						street: address?.address1 ? address.address1 : '',
						street_2: address?.address2 ? address.address2 : '',
						zip: address?.zip ? address.zip : '',
						city: address?.city ? address.city : '',
						state: address?.state ? address.state : '',
						country: 'USA',
						email: checkoutSuccess.userEmail
					}
				}
			]
		);

		window.letterpress(
			'addItem',
			checkoutSuccess.userId, // order ID
			'', // SKU
			'', // product name
			'', // category or variation
			checkoutSuccess.investmentAmount, // unit price
			'1', // quantity
			'USD' // currency
		);

		window.letterpress('trackTrans');
	}
};

export const trackConversionEvents = (checkoutSuccess: CheckoutSuccess): void => {
	const appStore = useAppStore();
	if (import.meta.env.VITE_ENABLE_CONVERSION_PIXELS === 'true' && !appStore.isThirdPartyLogin) {
		trackUetqConversion(checkoutSuccess);
		trackGoogleAnalyticsConversion(checkoutSuccess);

		if (checkoutSuccess.firstInvestmentForUser) {
			const totalValueNumber = Number(checkoutSuccess.investmentAmount || 0);
			const maximumValue = 25000;
			const totalValueWithoutOutliers = Math.min(totalValueNumber, maximumValue);
			trackPdstConversion(checkoutSuccess);
			trackGtagConversion(checkoutSuccess, totalValueNumber);
			trackFacebookQueryConversion(totalValueWithoutOutliers);
			trackLetterpressConversion(checkoutSuccess);
		}
	}
};

export const trackSignupEvents = (): void => {
	setItemInLocalStorage('fundrise__onWaitlist', 'true');
	const appStore = useAppStore();
	if (import.meta.env.VITE_ENABLE_CONVERSION_PIXELS && !appStore.isThirdPartyLogin) {
		if (window.gtag) {
			window.gtag({
				event: 'signup',
				send_to: 'AW-844391139/CP1-CM-jwZ8BEOPF0ZID'
			});
			window.gtag({ event: 'signup' });
		}

		if (hasFacebookQuery()) {
			window.fbq('track', 'CompleteRegistration', {
				accredited: 'false'
			});
		}

		if (window.twttr) {
			window.twttr.conversion.trackPid('nvok3', { tw_sale_amount: 0, tw_order_quantity: 0 });
		}
	}
};

export const getAppleAppStoreLink = (utmCampaign: string): string => {
	return `${import.meta.env.VITE_AVATAR_URL}/app-store-redirect?utm_source=fundrise&utm_medium=website&utm_campaign=${utmCampaign}`;
};
export const getGooglePlayStoreLink = (utmCampaign: string): string => {
	return `${import.meta.env.VITE_AVATAR_URL}/play-store-redirect?utm_source=fundrise&utm_medium=website&utm_campaign=${utmCampaign}`;
};
